/**
 * Vista: Listado de Remisiones (AMX-VTA-FR-06)
 *
 * Punto de entrada único del módulo. La creación se dispara desde el botón
 * "+ Nueva Remisión" del header (no desde otros módulos). El form de detalle
 * tiene su propio buscador por num_serie y el backend aplica el gate:
 *   1) tractor existe → 2) sin remisión activa → 3) FR-11 vigente.
 */

import remisionService from '../services/remision.service.js';
import permisos from '../services/permissions.service.js';
import modal from '../utils/modal.js';
import toast from '../utils/toast.js';
import router from '../router/router.js';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const fmtFecha = (f) => {
  if (!f) return '—';
  const [y, m, d] = String(f).substring(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : '—';
};

const fmtMoneda = (n, moneda = 'MXN') => {
  if (n === null || n === undefined) return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return `${moneda === 'USD' ? 'US$' : '$'} ${num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ESTADO_BADGE = {
  pendiente: { color: '#92400e', bg: '#fef3c7', label: 'Pendiente' },
  completada:{ color: '#065f46', bg: '#d1fae5', label: 'Completada' },
  cancelada: { color: '#991b1b', bg: '#fee2e2', label: 'Cancelada' },
  devuelta:  { color: '#92400e', bg: '#fef3c7', label: 'Devuelta' }
};

export default {
  data: {
    remisiones: [],
    loading: true,
    filters: { search: '', estado: '', fecha_desde: '', fecha_hasta: '' },
    stats: { total: 0, emitidas: 0, canceladas: 0, devueltas: 0 },
    pagination: { page: 1, limit: 20, total: 0, totalPages: 0 }
  },

  async render() {
    return `
      <div class="page-header" style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:1rem;">
        <div>
          <h1>Remisiones</h1>
          <small style="color:var(--gray-500);">Documentos de venta de tractor (AMX-VTA-FR-06).</small>
        </div>
        ${permisos.canCreate?.('remisiones') ? `
          <button class="btn btn-primary" onclick="window.currentView.nuevaRemision()">+ Nueva Remisión</button>
        ` : ''}
      </div>

      <div class="stats-grid" id="statsContainer">
        <div class="loading-spinner"></div>
      </div>

      <div class="filters-card">
        <div class="filters-grid">
          <div class="form-group">
            <label>Buscar</label>
            <input type="text" id="searchInput" placeholder="Folio, núm. serie o cliente..." class="form-control" />
          </div>
          <div class="form-group">
            <label>Estado</label>
            <select id="estadoFilter" class="form-control">
              <option value="">Todos</option>
              <option value="pendiente">Pendientes</option>
              <option value="completada">Completadas</option>
              <option value="cancelada">Canceladas</option>
              <option value="devuelta">Devueltas</option>
            </select>
          </div>
          <div class="form-group">
            <label>Desde</label>
            <input type="date" id="fechaDesde" class="form-control" />
          </div>
          <div class="form-group">
            <label>Hasta</label>
            <input type="date" id="fechaHasta" class="form-control" />
          </div>
          <div class="form-group" style="display:flex;align-items:flex-end;">
            <button class="btn btn-secondary" id="btnLimpiarFiltros">Limpiar</button>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th></th>
                <th>Folio</th>
                <th class="tbl-hide-mobile">Fecha</th>
                <th class="tbl-hide-mobile">Tractor</th>
                <th class="tbl-hide-mobile">Vendedor</th>
                <th class="tbl-hide-mobile">Total</th>
                <th class="tbl-hide-mobile">Estado</th>
                <th class="tbl-hide-mobile" style="text-align:right;">Acciones</th>
              </tr>
            </thead>
            <tbody id="remisionesTableBody">
              <tr>
                <td colspan="8" class="text-center">
                  <div class="loading-spinner"></div>
                  <p>Cargando remisiones...</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div id="paginationContainer" style="padding:1rem;"></div>
      </div>
    `;
  },

  async afterRender() {
    if (!permisos.canView('remisiones')) {
      document.getElementById('content').innerHTML = '<div style="padding:2rem;text-align:center;"><h2>Acceso denegado</h2></div>';
      return;
    }
    window.currentView = this;
    this.setupListeners();
    await this.cargar();
  },

  setupListeners() {
    document.getElementById('btnLimpiarFiltros')?.addEventListener('click', () => this.limpiarFiltros());
    let searchTO;
    document.getElementById('searchInput')?.addEventListener('input', (e) => {
      clearTimeout(searchTO);
      searchTO = setTimeout(() => {
        this.data.filters.search = e.target.value;
        this.data.pagination.page = 1;
        this.cargar();
      }, 300);
    });
    ['estadoFilter', 'fechaDesde', 'fechaHasta'].forEach(id => {
      document.getElementById(id)?.addEventListener('change', (e) => {
        const f = this.data.filters;
        if (id === 'estadoFilter') f.estado = e.target.value;
        if (id === 'fechaDesde') f.fecha_desde = e.target.value;
        if (id === 'fechaHasta') f.fecha_hasta = e.target.value;
        this.data.pagination.page = 1;
        this.cargar();
      });
    });
  },

  limpiarFiltros() {
    this.data.filters = { search: '', estado: '', fecha_desde: '', fecha_hasta: '' };
    document.getElementById('searchInput').value = '';
    document.getElementById('estadoFilter').value = '';
    document.getElementById('fechaDesde').value = '';
    document.getElementById('fechaHasta').value = '';
    this.data.pagination.page = 1;
    this.cargar();
  },

  async cargar() {
    try {
      const params = {
        page: this.data.pagination.page,
        limit: this.data.pagination.limit,
        ...Object.fromEntries(Object.entries(this.data.filters).filter(([_, v]) => v))
      };
      const [resList, resStats] = await Promise.all([
        remisionService.listar(params),
        remisionService.obtenerEstadisticas()
      ]);
      const dataList = resList?.data || resList;
      this.data.remisiones = dataList.remisiones || [];
      this.data.pagination = dataList.pagination || this.data.pagination;
      this.data.stats = resStats?.data || resStats || this.data.stats;
      this.renderEstadisticas();
      this.renderTabla();
    } catch (err) {
      toast.error(err.message || 'Error al cargar remisiones');
    }
  },

  renderEstadisticas() {
    const s = this.data.stats;
    const c = document.getElementById('statsContainer');
    if (!c) return;
    c.innerHTML = `
      <div class="stat-card">
        <div class="stat-icon">📄</div>
        <div class="stat-content"><div class="stat-value">${s.total || 0}</div><div class="stat-label">Total</div></div>
      </div>
      <div class="stat-card stat-info">
        <div class="stat-icon">📝</div>
        <div class="stat-content"><div class="stat-value">${s.pendientes || 0}</div><div class="stat-label">Pendientes</div></div>
      </div>
      <div class="stat-card stat-success">
        <div class="stat-icon">✅</div>
        <div class="stat-content"><div class="stat-value">${s.completadas || 0}</div><div class="stat-label">Completadas</div></div>
      </div>
      <div class="stat-card stat-warning">
        <div class="stat-icon">↩️</div>
        <div class="stat-content"><div class="stat-value">${s.devueltas || 0}</div><div class="stat-label">Devueltas</div></div>
      </div>
      <div class="stat-card stat-danger">
        <div class="stat-icon">⛔</div>
        <div class="stat-content"><div class="stat-value">${s.canceladas || 0}</div><div class="stat-label">Canceladas</div></div>
      </div>
    `;
  },

  renderTabla() {
    const tbody = document.getElementById('remisionesTableBody');
    if (!tbody) return;
    if (this.data.remisiones.length === 0) {
      tbody.innerHTML = '<tr><td colspan="8" class="text-center" style="padding:2rem;color:var(--gray-500);">Sin remisiones.</td></tr>';
      this.renderPagination();
      return;
    }
    tbody.innerHTML = this.data.remisiones.map(r => {
      // Estado virtual del badge:
      //   - completada=0 + estado=emitida → "Pendiente"
      //   - completada=1 + estado=emitida → "Completada"
      //   - estado=cancelada/devuelta → su propio badge
      const esPendiente = (r.completada === 0 || r.completada === false) && r.estado === 'emitida';
      let badgeKey;
      if (esPendiente) badgeKey = 'pendiente';
      else if (r.estado === 'emitida') badgeKey = 'completada';
      else badgeKey = r.estado;
      const badge = ESTADO_BADGE[badgeKey] || { color: '#374151', bg: '#e5e7eb', label: r.estado };
      const badgeHtml = `<span style="padding:0.15rem 0.5rem;border-radius:4px;font-size:0.75rem;font-weight:600;background:${badge.bg};color:${badge.color};">${badge.label}</span>`;
      const tractorTxt = `${r.tractor_marca || r.marca || ''} ${r.tractor_modelo || r.modelo || ''}`.trim();
      // Acciones por estado (texto en lugar de iconos, patrón EntregasList):
      //   - Pendiente → Editar + Eliminar.
      //   - Completada (emitida) → Ver + Cancelar (admin).
      //   - Cancelada/Devuelta → solo Ver.
      const btnPrincipal = esPendiente && permisos.canEdit('remisiones')
        ? `<button class="btn btn-sm btn-primary" onclick="window.currentView.verDetalle(${r.id})">Editar</button>`
        : `<button class="btn btn-sm btn-primary" onclick="window.currentView.verDetalle(${r.id})">Ver</button>`;
      const accionesHtml = `
        ${btnPrincipal}
        ${esPendiente && permisos.canDelete('remisiones') ? `
          <button class="btn btn-sm btn-danger" onclick="window.currentView.eliminar(${r.id})">Eliminar</button>
        ` : ''}
        ${!esPendiente && r.estado === 'emitida' && permisos.can('remisiones', 'cancelar') ? `
          <button class="btn btn-sm btn-warning" onclick="window.currentView.cancelar(${r.id})">Cancelar</button>
        ` : ''}
      `;
      return `
        <tr style="cursor:pointer;" onclick="window.currentView.verDetalle(${r.id})">
          <td onclick="event.stopPropagation();"><button class="tbl-expand-btn" onclick="window.currentView.toggleRow(${r.id})">&#9654;</button></td>
          <td><strong>${esc(r.numero_remision)}</strong></td>
          <td class="tbl-hide-mobile">${fmtFecha(r.fecha_remision)}</td>
          <td class="tbl-hide-mobile"><code>${esc(r.num_serie)}</code><br><small style="color:var(--gray-500);">${esc(tractorTxt)}</small></td>
          <td class="tbl-hide-mobile">${esc(r.vendedor_nombre || '—')}</td>
          <td class="tbl-hide-mobile" style="font-weight:600;">${fmtMoneda(r.total, r.moneda)}</td>
          <td class="tbl-hide-mobile">${badgeHtml}</td>
          <td class="tbl-hide-mobile" style="text-align:right;" onclick="event.stopPropagation();">
            ${accionesHtml}
          </td>
        </tr>
        <tr class="tbl-detail-row" id="detail-rem-${r.id}">
          <td colspan="8" class="tbl-detail-cell">
            <dl class="tbl-detail-grid">
              <dt>Fecha</dt><dd>${fmtFecha(r.fecha_remision)}</dd>
              <dt>Tractor</dt><dd><code>${esc(r.num_serie)}</code><br><small style="color:var(--gray-500);">${esc(tractorTxt)}</small></dd>
              <dt>Vendedor</dt><dd>${esc(r.vendedor_nombre || '—')}</dd>
              <dt>Total</dt><dd style="font-weight:600;">${fmtMoneda(r.total, r.moneda)}</dd>
              <dt>Estado</dt><dd>${badgeHtml}</dd>
            </dl>
            <div style="margin-top:var(--spacing-sm);display:flex;gap:var(--spacing-xs);flex-wrap:wrap;">${accionesHtml}</div>
          </td>
        </tr>
      `;
    }).join('');
    this.renderPagination();
  },

  toggleRow(id) {
    const detailRow = document.getElementById(`detail-rem-${id}`);
    const btn = detailRow?.previousElementSibling?.querySelector('.tbl-expand-btn');
    if (detailRow) {
      detailRow.classList.toggle('open');
      btn?.classList.toggle('open');
    }
  },

  nuevaRemision() {
    // Navega al form de creación. El form tiene su propio buscador de
    // num_serie integrado (patrón ChecklistDocumentosDetalle / EntregaDetalle).
    router.navigate('/ventas/remisiones/nueva');
  },

  renderPagination() {
    const c = document.getElementById('paginationContainer');
    if (!c) return;
    const p = this.data.pagination;
    if (p.totalPages <= 1) { c.innerHTML = ''; return; }
    c.innerHTML = `
      <div style="display:flex;gap:0.5rem;align-items:center;justify-content:flex-end;">
        <button class="btn btn-sm" ${p.page === 1 ? 'disabled' : ''} onclick="window.currentView.irPagina(${p.page - 1})">← Anterior</button>
        <span style="font-size:0.85rem;color:var(--gray-700);">Página ${p.page} de ${p.totalPages} (${p.total} totales)</span>
        <button class="btn btn-sm" ${p.page >= p.totalPages ? 'disabled' : ''} onclick="window.currentView.irPagina(${p.page + 1})">Siguiente →</button>
      </div>
    `;
  },

  irPagina(p) {
    this.data.pagination.page = p;
    this.cargar();
  },

  verDetalle(id) {
    router.navigate(`/ventas/remisiones/${id}`);
  },

  async cancelar(id) {
    const r = this.data.remisiones.find(x => x.id === id);
    const titulo = r ? `${r.numero_remision} (${r.num_serie})` : `#${id}`;
    modal.open('Cancelar remisión',
      `<p>Vas a cancelar la remisión <strong>${esc(titulo)}</strong>.</p>
       <p style="margin-top:0.5rem;color:var(--gray-700);">Quedará marcada como <strong>cancelada</strong> con el motivo.</p>
       <div class="form-group" style="margin-top:1rem;">
         <label>Motivo de cancelación *</label>
         <textarea id="motivoCancelacion" class="form-control" rows="3" placeholder="Ej: error en datos del cliente, cliente desistió, etc." required></textarea>
       </div>`,
      [
        { text: 'Cerrar', class: 'btn-secondary' },
        {
          text: 'Cancelar remisión', class: 'btn-danger',
          closeOnClick: false,
          onClick: async () => {
            const motivo = document.getElementById('motivoCancelacion')?.value?.trim();
            if (!motivo) { toast.warning('Indica el motivo'); return; }
            try {
              await remisionService.cancelar(id, motivo);
              modal.close();
              toast.success('Remisión cancelada');
              await this.cargar();
            } catch (err) {
              toast.error(err.message || 'Error al cancelar');
            }
          }
        }
      ]
    );
  },

  async eliminar(id) {
    const r = this.data.remisiones.find(x => x.id === id);
    const titulo = r ? `${r.numero_remision} (${r.num_serie})` : `#${id}`;
    modal.confirm('Confirmar eliminación',
      `<p>¿Estás seguro de eliminar la remisión <strong>${esc(titulo)}</strong>? Esta acción no se puede deshacer.</p>`,
      async () => {
        try {
          await remisionService.eliminar(id);
          toast.success('Remisión eliminada');
          await this.cargar();
        } catch (err) {
          toast.error(err.message || 'Error al eliminar');
        }
      }
    );
  }
};
